/**
 * Post-login redirect guard.
 *
 * The OAuth consent flow bounces an unauthenticated browser to
 * `/login?next=/oauth/authorize?…`; after sign-in the login page sends it back
 * with a full page load. A Signal brief link opened while signed out lands on
 * `/login?next=/brief/<uuid>` the same way. `next` is attacker-controllable
 * (anyone can craft the link), so it is honoured only when it is a same-origin
 * path to the authorize endpoint or exactly `/brief/<uuid>`. Everything else —
 * absolute URLs, protocol-relative `//host`, backslash tricks browsers
 * normalise to `//`, fragments, whitespace, any other page — is rejected and
 * the caller falls back to `/`.
 */

/** Path-and-query characters only (RFC 3986 unreserved, sub-delims, `:@%/?`). No `#`, no whitespace. */
const SAFE_PATH = /^\/[A-Za-z0-9\-._~!$&'()*+,;=:@%\/?]*$/;

const AUTHORIZE_PREFIX = '/oauth/authorize?';

/** A brief page: the whole path, a lowercase-or-uppercase uuid, nothing after it. */
const BRIEF_PATH = /^\/brief\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Returns `raw` when it is a safe same-origin authorize URL or brief page, else null. */
export function safeNext(raw: string | null): string | null {
  if (raw === null) return null;
  if (BRIEF_PATH.test(raw)) return raw;
  if (!raw.startsWith(AUTHORIZE_PREFIX)) return null;
  if (!SAFE_PATH.test(raw)) return null;
  if (raw.includes('\\')) return null;
  if (raw.startsWith('//')) return null;
  if (raw.includes('://')) return null;
  return raw;
}
